<template>
  <div class="documentation">
    <!-- 🎯 Header -->
    <v-card class="mb-6" elevation="2">
      <v-card-title class="pa-6 bg-gradient-to-r from-green-500 to-teal-600 text-white">
        <div class="d-flex align-center">
          <v-icon icon="mdi-book-open" size="32" class="me-3" />
          <div>
            <h2 class="text-h4 mb-0">📚 Documentación MGI</h2>
            <p class="text-subtitle-1 mb-0 opacity-90">Guías, APIs y referencias técnicas</p>
          </div>
        </div>
      </v-card-title>
    </v-card>

    <!-- 🔍 Search & Filter -->
    <v-card class="mb-6" elevation="1">
      <v-card-text class="pa-4">
        <v-row align="center">
          <v-col cols="12" md="6">
            <v-text-field
              v-model="searchQuery"
              label="Buscar en documentación"
              variant="outlined"
              density="compact"
              prepend-inner-icon="mdi-magnify"
              clearable
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedCategory"
              :items="categories"
              item-title="label"
              item-value="value"
              label="Categoría"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="selectedType"
              :items="documentTypes"
              item-title="label"
              item-value="value"
              label="Tipo"
              variant="outlined"
              density="compact"
              hide-details
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 📊 Quick Stats -->
    <v-row class="mb-6">
      <v-col cols="12" md="3" v-for="stat in docStats" :key="stat.label">
        <v-card elevation="1" class="text-center pa-4">
          <v-avatar :color="stat.color" size="48" class="mb-2">
            <v-icon :icon="stat.icon" color="white" size="24" />
          </v-avatar>
          <h3 class="text-h5 mb-1">{{ stat.value }}</h3>
          <p class="text-caption text-medium-emphasis">{{ stat.label }}</p>
        </v-card>
      </v-col>
    </v-row>

    <!-- 📑 Documentation Sections -->
    <v-row>
      <v-col
        v-for="section in filteredDocs"
        :key="section.id"
        cols="12"
        md="6"
        lg="4"
      >
        <v-card
          :class="['doc-card', `doc-${section.category}`]"
          elevation="2"
          @click="openDocument(section)"
        >
          <v-card-title class="pa-4">
            <div class="d-flex align-center">
              <v-avatar :color="section.color" size="40" class="me-3">
                <v-icon :icon="section.icon" color="white" />
              </v-avatar>
              <div class="flex-grow-1">
                <h3 class="text-h6 mb-0">{{ section.title }}</h3>
                <p class="text-caption mb-0 text-medium-emphasis">{{ section.category }}</p>
              </div>
              <v-chip
                :color="section.status === 'updated' ? 'success' : section.status === 'draft' ? 'warning' : 'info'"
                size="small"
                variant="flat"
              >
                {{ section.status }}
              </v-chip>
            </div>
          </v-card-title>

          <v-card-text class="pa-4 pt-0">
            <p class="text-body-2 mb-3">{{ section.description }}</p>
            
            <!-- 📊 Document Info -->
            <div class="mb-3">
              <v-chip-group>
                <v-chip
                  v-for="tag in section.tags"
                  :key="tag"
                  size="small"
                  variant="outlined"
                  :color="section.color"
                >
                  {{ tag }}
                </v-chip>
              </v-chip-group>
            </div>

            <!-- 📈 Stats -->
            <div class="d-flex justify-space-between text-caption text-medium-emphasis">
              <span><strong>{{ section.pages }}</strong> páginas</span>
              <span>Actualizado: {{ formatDate(section.lastUpdate) }}</span>
            </div>
          </v-card-text>

          <v-card-actions class="pa-4 pt-0">
            <v-btn
              variant="outlined"
              size="small"
              @click.stop="previewDocument(section)"
            >
              Vista previa
            </v-btn>
            <v-spacer />
            <v-btn
              variant="flat"
              :color="section.color"
              size="small"
              @click.stop="openDocument(section)"
            >
              Abrir
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>

    <!-- 🔧 API Reference Quick Access -->
    <v-card class="mt-6" elevation="1">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-api" class="me-2" />
        Referencias Rápidas de API
      </v-card-title>
      <v-card-text class="pa-4">
        <v-row>
          <v-col
            v-for="api in apiReferences"
            :key="api.name"
            cols="12"
            md="6"
            lg="3"
          >
            <v-card variant="outlined" class="pa-3 text-center">
              <v-icon :icon="api.icon" :color="api.color" size="32" class="mb-2" />
              <h4 class="text-subtitle-1 mb-1">{{ api.name }}</h4>
              <p class="text-caption mb-2 text-medium-emphasis">{{ api.endpoints }} endpoints</p>
              <v-btn
                variant="outlined"
                :color="api.color"
                size="small"
                @click="openApiDocs(api)"
              >
                Ver API
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 📱 Quick Links -->
    <v-card class="mt-6" elevation="1">
      <v-card-title class="pa-4 bg-surface-variant">
        <v-icon icon="mdi-link-variant" class="me-2" />
        Enlaces Rápidos
      </v-card-title>
      <v-card-text class="pa-4">
        <v-row>
          <v-col
            v-for="link in quickLinks"
            :key="link.name"
            cols="12"
            md="4"
          >
            <v-list-item
              :prepend-icon="link.icon"
              :title="link.name"
              :subtitle="link.description"
              @click="openExternalLink(link)"
            >
              <template #append>
                <v-icon icon="mdi-open-in-new" color="primary" />
              </template>
            </v-list-item>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 🔄 Back to Dev Center -->
    <v-card class="mt-6" elevation="1">
      <v-card-actions class="pa-4">
        <v-btn
          variant="outlined"
          prepend-icon="mdi-arrow-left"
          @click="$router.push('/dev-center')"
        >
          Volver al Dev Center
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          @click="createNewDoc"
        >
          Nueva Documentación
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useMainStore } from '@/stores/main'

// 🎯 Types
interface DocSection {
  id: string
  title: string
  category: string
  description: string
  icon: string
  color: string
  status: 'updated' | 'draft' | 'stable'
  tags: string[]
  pages: number
  lastUpdate: Date
}

interface ApiReference {
  name: string
  icon: string
  color: string
  endpoints: number
  url: string
}

interface QuickLink {
  name: string
  description: string
  icon: string
  url: string
  external?: boolean
}

// 🔧 Reactive state
const store = useMainStore()
const searchQuery = ref('')
const selectedCategory = ref('')
const selectedType = ref('')

// 📊 Mock data
const docStats = ref([
  { label: 'Documentos Totales', value: '127', icon: 'mdi-file-document', color: 'primary' },
  { label: 'APIs Documentadas', value: '43', icon: 'mdi-api', color: 'success' },
  { label: 'Guías Completas', value: '18', icon: 'mdi-book', color: 'info' },
  { label: 'Actualizaciones', value: '9', icon: 'mdi-update', color: 'warning' }
])

const categories = ref([
  { label: 'Todas las categorías', value: '' },
  { label: 'Desarrollo', value: 'development' },
  { label: 'APIs', value: 'api' },
  { label: 'Configuración', value: 'config' },
  { label: 'Despliegue', value: 'deployment' },
  { label: 'Usuarios', value: 'user' }
])

const documentTypes = ref([
  { label: 'Todos los tipos', value: '' },
  { label: 'Guía', value: 'guide' },
  { label: 'Referencia', value: 'reference' },
  { label: 'Tutorial', value: 'tutorial' },
  { label: 'API Docs', value: 'api' }
])

const documentation = ref<DocSection[]>([
  {
    id: '1',
    title: 'Guía de Inicio Rápido',
    category: 'Desarrollo',
    description: 'Configuración inicial y primeros pasos en el sistema MGI',
    icon: 'mdi-rocket-launch',
    color: 'primary',
    status: 'updated',
    tags: ['Inicio', 'Setup', 'Básico'],
    pages: 12,
    lastUpdate: new Date('2024-01-22')
  },
  {
    id: '2',
    title: 'API de Autenticación',
    category: 'APIs',
    description: 'Documentación completa del sistema de autenticación y autorización',
    icon: 'mdi-shield-key',
    color: 'success',
    status: 'stable',
    tags: ['Auth', 'JWT', 'Seguridad'],
    pages: 24,
    lastUpdate: new Date('2024-01-20')
  },
  {
    id: '3',
    title: 'Configuración de Roles',
    category: 'Configuración',
    description: 'Cómo configurar y gestionar roles de usuario en el sistema',
    icon: 'mdi-account-group',
    color: 'purple',
    status: 'updated',
    tags: ['Roles', 'Permisos', 'RBAC'],
    pages: 18,
    lastUpdate: new Date('2024-01-21')
  },
  {
    id: '4',
    title: 'Despliegue en Producción',
    category: 'Despliegue',
    description: 'Guía paso a paso para desplegar el sistema en entornos de producción',
    icon: 'mdi-cloud-upload',
    color: 'orange',
    status: 'draft',
    tags: ['Deploy', 'Producción', 'Docker'],
    pages: 8,
    lastUpdate: new Date('2024-01-19')
  },
  {
    id: '5',
    title: 'Manual de Usuario',
    category: 'Usuarios',
    description: 'Manual completo para usuarios finales del sistema',
    icon: 'mdi-account-circle',
    color: 'teal',
    status: 'stable',
    tags: ['Usuario', 'Manual', 'UI/UX'],
    pages: 45,
    lastUpdate: new Date('2024-01-18')
  },
  {
    id: '6',
    title: 'API de Incidencias',
    category: 'APIs',
    description: 'Endpoints para gestión de incidencias y reportes',
    icon: 'mdi-alert-circle',
    color: 'red',
    status: 'updated',
    tags: ['Incidencias', 'REST', 'CRUD'],
    pages: 32,
    lastUpdate: new Date('2024-01-22')
  }
])

const apiReferences = ref<ApiReference[]>([
  { name: 'Auth API', icon: 'mdi-shield-key', color: 'green', endpoints: 8, url: '/api/auth' },
  { name: 'Users API', icon: 'mdi-account', color: 'blue', endpoints: 12, url: '/api/users' },
  { name: 'Incidents API', icon: 'mdi-alert', color: 'orange', endpoints: 15, url: '/api/incidents' },
  { name: 'Reports API', icon: 'mdi-chart-line', color: 'purple', endpoints: 9, url: '/api/reports' }
])

const quickLinks = ref<QuickLink[]>([
  {
    name: 'GitHub Repository',
    description: 'Código fuente del proyecto',
    icon: 'mdi-github',
    url: 'https://github.com/mgi-system',
    external: true
  },
  {
    name: 'Postman Collection',
    description: 'Colección de APIs para testing',
    icon: 'mdi-api',
    url: '/docs/postman',
    external: true
  },
  {
    name: 'Environment Variables',
    description: 'Variables de entorno requeridas',
    icon: 'mdi-cog',
    url: '/docs/env-vars'
  },
  {
    name: 'Troubleshooting',
    description: 'Resolución de problemas comunes',
    icon: 'mdi-help-circle',
    url: '/docs/troubleshooting'
  },
  {
    name: 'Changelog',
    description: 'Historial de cambios y versiones',
    icon: 'mdi-history',
    url: '/docs/changelog'
  },
  {
    name: 'Contributing Guide',
    description: 'Guía para contribuir al proyecto',
    icon: 'mdi-account-plus',
    url: '/docs/contributing'
  }
])

const filteredDocs = computed(() => {
  let filtered = documentation.value

  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(doc => 
      doc.title.toLowerCase().includes(query) ||
      doc.description.toLowerCase().includes(query) ||
      doc.tags.some(tag => tag.toLowerCase().includes(query))
    )
  }

  if (selectedCategory.value) {
    filtered = filtered.filter(doc => 
      doc.category.toLowerCase() === selectedCategory.value
    )
  }

  return filtered
})

// 🎯 Methods
const openDocument = (doc: DocSection) => {
  console.log('📖 Abriendo documento:', doc.title)
  store.showNotification({
    type: 'info',
    title: 'Documento Abierto',
    message: `Abriendo: ${doc.title}`
  })
}

const previewDocument = (doc: DocSection) => {
  console.log('👁️ Vista previa:', doc.title)
  store.showNotification({
    type: 'info',
    title: 'Vista Previa',
    message: `Previsualizando: ${doc.title}`
  })
}

const openApiDocs = (api: ApiReference) => {
  console.log('🔌 Abriendo API docs:', api.name)
  store.showNotification({
    type: 'info',
    title: 'API Documentation',
    message: `Abriendo documentación de ${api.name}`
  })
}

const openExternalLink = (link: QuickLink) => {
  console.log('🔗 Abriendo enlace:', link.name)
  store.showNotification({
    type: 'info',
    title: 'Enlace Externo',
    message: `Abriendo: ${link.name}`
  })
}

const createNewDoc = () => {
  console.log('➕ Creando nueva documentación')
  store.showNotification({
    type: 'success',
    title: 'Nueva Documentación',
    message: 'Creando nuevo documento...'
  })
}

const formatDate = (date: Date) => {
  return date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

// 🎬 Lifecycle
onMounted(() => {
  console.log('📚 Vista de documentación cargada')
})
</script>

<style scoped>
.documentation {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.doc-card {
  transition: all 0.3s ease;
  cursor: pointer;
  border-left: 4px solid transparent;
}

.doc-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
}

.doc-development {
  border-left-color: #2196f3;
}

.doc-api {
  border-left-color: #4caf50;
}

.doc-config {
  border-left-color: #9c27b0;
}

.doc-deployment {
  border-left-color: #ff9800;
}

.doc-user {
  border-left-color: #009688;
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #4caf50 0%, #009688 100%);
}
</style>
